<script>
    import { onMount, tick, untrack } from 'svelte';
    import { fade } from 'svelte/transition';
    import { format, locale } from 'svelte-i18n';
    import { params } from '@roxi/routify';
    import * as bootstrap from "bootstrap";
    import dayjs from 'dayjs';
    import relativeTime from 'dayjs/plugin/relativeTime';
    import localizedFormat from 'dayjs/plugin/localizedFormat';
    import 'dayjs/locale/es';
    import 'dayjs/locale/en';

    import findingService from "../../finding/v2-service.js";
    import vulnerabilityService from "../service.js";
    import BreadCrumNav from "../../../components/breadcrum-nav.svelte";
    import Finding from "../../finding/_components/list.svelte";
    import VulnerabilityDetail from '../_components/vulnerability-detail.svelte';
    import Form from '../custom/_form.svelte';
    import Properties from '../../../components/properties.svelte';
    import AssetsApi from '../../asset/assets.api.js';
    import Spinner from '../../../components/spinner.svelte';
    import tagService from '../../tag/service.js';
    import Tag from '../../../components/tags.svelte';
    import Note from '../_components/note.svelte';
    import AddNote from '../_components/note-add.svelte';
    import { notificate } from '../../../services/notification';
    import PropNameRow from '../../../components/powerTable/prop-name-row.svelte';
    import PropValueRow from '../../../components/powerTable/prop-value-row.svelte';
    import PropKindRow from '../../../components/powerTable/prop-kind-row.svelte';
    import TagForm from "../../tag/_form.svelte";
    import { searchFindings, findings } from "../../finding/findings.state.svelte.js";
    import { token, user } from "../../user/user.state.svelte.js";

    dayjs.locale($locale);
    dayjs.extend(relativeTime);
    dayjs.extend(localizedFormat);

    let { id: propId = null } = $props();
    let id = $derived(propId || $params?.id);
    let user_is_internal = $derived(user.data?.is_internal || false);

    let vulnerability = $state(null);
    let note = $state({ content: "" });
    let tags = $state([]);
    let allTags = $state([]);
    let properties = $state([]);
    let assets = $state([]);
    
    let is_loading = $state(true);
    let is_loading_props = $state(false);
    let is_loading_tags = $state(false);
    let is_loading_findings = $state(false);
    let is_loading_note = $state(false);
    
    let showProperties = $state(true);
    let showNoteForm = $state(false);
    let showTagsForm = $state(false);
    let showTagsDeleteForm = $state(false);
    let table_is_expanded = $state(false);
    let formData = $state({});
    let lang = $state($locale || 'es');
    let isDropdownOpen = $state(false);
    let reload_tags = $state(false);

    let customVulFormModal;
    let tagModal;

    let propInstructs = $state([
        { key: 'kind', title: $format("list.columns.kind"), filterable: false, parseAs: "component", dataComponent: PropKindRow },
        { key: 'name', title: $format("list.columns.name"), filterable: false, parseAs: 'component', dataComponent: PropNameRow },
        { key: 'value', title: $format("list.columns.value"), filterable: false, parseAs: 'component', dataComponent: PropValueRow }
    ]);

    const clickOutside = (node, callback) => {
        const handleClick = (event) => {
            if (node && !node.contains(event.target) && !event.defaultPrevented) {
                callback();
            }
        };
        document.addEventListener("click", handleClick, true);
        return {
            destroy() {
                document.removeEventListener("click", handleClick, true);
            },
        };
    };

    const loadVulnerability = async (vulnId) => {
        if (!vulnId) return;
        try {
            let pVul = await vulnerabilityService.find('v2/vulnerability', vulnId);
            vulnerability = pVul;
            formData = {
                id: pVul.id,
                title: pVul.title,
                title_es: pVul.title_es,
                summary: pVul.summary,
                summary_es: pVul.summary_es,
                cwe_id: pVul.cwe_id,
                cvss3_vector: pVul.cvss3_vector,
                references: pVul.references || []
            };
        } catch (err) {
            console.error("Error loading vulnerability:", err);
        }
    };

    const loadNote = async (vulnId) => {
        if (!token.access_token || !vulnId) {
            note = { content: "" };
            return;
        }
        is_loading_note = true;
        try {
            note = await vulnerabilityService.getNote(vulnId);
        } catch (err) {
            note = { content: "" };
        }
        is_loading_note = false;
    };

    const loadProperties = async (vulnId) => {
        if (!token.access_token || !vulnId) return;
        is_loading_props = true;
        try {
            let propsRes = await vulnerabilityService.search('v2/vulnerability-property/', { filter: { vulnerability_id: vulnId } });
            for (let prop of propsRes) {
                if (prop.location === "") prop.location = 'General';
            }
            properties = propsRes;
        } catch (err) {
            properties = [];
        }
        is_loading_props = false;
    };

    const loadTags = async (vulnId) => {
        if (!token.access_token || !vulnId) return { tags: [], associations: [], allTags: [] };
        is_loading_tags = true;
        try {
            let associations = await tagService.search('v2/tag/association/', {});
            let fetchedAllTags = await tagService.search('v2/tag/', { filter: {} });
            allTags = fetchedAllTags;
            let ids = associations
                .filter(assoc => assoc.target_object_type === 'Vulnerability' && assoc.target_object_id === vulnId)
                .map(assoc => assoc.tag_id);
            tags = allTags.filter(t => ids.includes(t.id));
            is_loading_tags = false;
            return { tags, associations, allTags };
        } catch (err) {
            is_loading_tags = false;
            return { tags: [], associations: [], allTags: [] };
        }
    };

    const loadFindings = async (vulnId) => {
        if (!token.access_token || !vulnId) return;
        is_loading_findings = true;
        try {
            let tagsData = await loadTags(vulnId);
            await searchFindings('v2/finding/', {
                ignore_loader: true,
                filter: { vulnerability_id: vulnId },
                buildList: { tags: { elements: tagsData.allTags, associations: tagsData.associations } },
                ignoreLoader: true
            });
        } catch (err) {
            console.error("Error loading findings:", err);
        }
        is_loading_findings = false;
    };

    const loadAssets = async () => {
        try {
            assets = await AssetsApi.fetchAssets('v2/asset/', {});
        } catch (err) {
            assets = [];
        }
    };

    const loadAllData = async (vulnId) => {
        is_loading = true;
        await Promise.all([
            loadVulnerability(vulnId),
            loadProperties(vulnId),
            loadNote(vulnId),
            loadFindings(vulnId),
            loadAssets()
        ]);
        is_loading = false;
    };

    const closeTagModal = () => {
        if (tagModal) tagModal.hide();
    };

    const addNewTag = (event) => {
        closeTagModal();
        return handleTagEdit({ detail: event.detail.tag ? event.detail.tag : event.detail.savedTag.id });
    };

    const handleTagEdit = (event) => {
        formData.tag = event.detail;
        if (!formData.tag || formData.tag === 'cancel' || !id) return;
        let reqData = {
            tag_id: formData.tag,
            target_object_type: "Vulnerability",
            target_object_id: id
        };
        tagService.saveAssociation(reqData, [], {})
            .catch(() => false)
            .then(async () => {
                showTagsForm = false;
                await loadTags(id);
            });
    };

    const handleTagDelete = (event) => {
        formData.tag = event.detail;
        if (!formData.tag || formData.tag === 'cancel' || !id) return;
        let reqData = {
            tag_id: formData.tag.tag,
            target_object_type: "Vulnerability",
            target_object_id: id
        };
        tagService.deleteAssociation(reqData, [], {})
            .catch(() => false)
            .then(async () => {
                showTagsForm = false;
                showTagsDeleteForm = false;
                await loadTags(id);
            });
    };

    const handleTagsReset = () => {
        showTagsForm = false;
        showTagsDeleteForm = false;
    };

    const toggleCollapsibleItems = (clicked) => {
        if (clicked === "properties") {
            showProperties = !showProperties;
        }
    };

    const showRows = () => {
        table_is_expanded = !table_is_expanded;
    };

    const handleAdd = async () => {
        if (customVulFormModal) customVulFormModal.hide();
        await loadVulnerability(id);
    };

    const changeLanguage = (currLang) => {
        lang = currLang === 'es' ? 'en' : 'es';
    };

    const deleteNote = async () => {
        let result = await notificate('confirm_delete', "modal", { massive: false });
        if (!result.isConfirmed || !id) return false;
        await vulnerabilityService.deleteNote(id, {});
        await loadNote(id);
    };

    onMount(async () => {
        await tick();
        const bs = typeof bootstrap !== "undefined" ? bootstrap : window.bootstrap;
        if (bs) {
            const customEl = document.getElementById("custom-add-modal");
            if (customEl) customVulFormModal = new bs.Modal(customEl);

            const tagEl = document.getElementById("tag-add-new-modal");
            if (tagEl) {
                tagModal = new bs.Modal(tagEl);
                tagEl.addEventListener("hidden.bs.modal", () => reload_tags = !reload_tags);
            }
        }
    });

    $effect(() => {
        if (id) {
            untrack(() => {
                loadAllData(id);
            });
        }
    });
</script>

<style>
    .assets-total {
        text-align: center;
        font-size: 1.8em;
        height: 4em;
    }
    .d-flex > * {
        flex: 1;
    }
    .flex-container-2item div:first-child {
        flex: 2;
    }
    .card + .card {
        margin-left: 14px;
    }
    .breadcrumb {
        padding: 0 15px !important;
    }
    .references-list {
        line-height: 1.5;
    }
    h4 small {
        font-size: 0.75rem;
        display: block;
    }
    .btn-delete-vulnerability {
        color: rgba(160, 33, 40, 0.9);
    }
    .btn-collapse {
        border: none;
        background-color: transparent;
    }
    .dropdown-toggle:after {
        content: none !important;
    }
    .btn-secondary.dropdown-toggle,
    .btn-secondary:not(:disabled):not(.disabled):active,
    .btn-secondary:not(:disabled):not(.disabled).active,
    .show > .btn-secondary.dropdown-toggle {
        background-color: rgba(248, 249, 250, 0.85) !important;
    }
    .is-selected {
        background-color: #f8f9fa !important;
        color: #0056b3 !important;
    }
</style>

<!-- VULS MODAL -->
<div class="modal fade" id="custom-add-modal" tabindex="-1" aria-hidden="true">
    <div class="modal-dialog" style="max-width: 80% !important; width: 80%;">
        <div class="modal-content">
            <div class="modal-header">
                <h4 class="modal-title">Editar Vulnerabilidad Privada <small>(ningún campo es requerido)</small></h4>
                <button type="button" class="btn-close" aria-label="Close" onclick={() => customVulFormModal && customVulFormModal.hide()}></button>
            </div>
            <div class="modal-body">
                {#if vulnerability}
                    <!-- on:cancel clears input in case Form dispatches it natively -->
                    <Form {formData} references={formData.references || [""]} on:save={handleAdd} onsave={handleAdd} on:cancel={() => customVulFormModal && customVulFormModal.hide()} oncancel={() => customVulFormModal && customVulFormModal.hide()} />
                {/if}
            </div>
        </div>
    </div>
</div>
<!-- FIN VULS MODAL-->

<section class="content-header">
    <div class="container-fluid">
        <div class="row">
            <div class="col-12">
                <h1 class="mt-3 mb-3 page-title">
                    {#if vulnerability}
                        {#if vulnerability.level !== 'good'}
                            <span class="cvss-badge cvss-{vulnerability.level || 'none'}" style="margin-right: .5em;">
                                {findingService.labels[vulnerability.level] || ''}
                            </span>
                        {/if}
                        {#if lang === 'es' && $locale === "es"}
                            {vulnerability.title_es || vulnerability.title || vulnerability.id}
                        {:else}
                            {vulnerability.title || vulnerability.id}
                        {/if}
                    {/if}
                </h1>
            </div>
            <div class="col-12 mt-3 breadcrumb">
                <BreadCrumNav routes={[{ path: id && id.includes('X-') && user_is_internal ? '/vulnerability/custom' : '/vulnerability', label: id && id.includes('X-') && user_is_internal ? 'Vulnerabilidades privadas' : 'Vulnerabilidades' }]} />
            </div>
        </div>
    </div>
</section>

<section class="content">
    <div class="container-fluid">
        <div class="row">
            <div class="col-12">
                {#if vulnerability}
                    <VulnerabilityDetail {vulnerability} navigation={false} on:changeLanguage={() => changeLanguage(lang)} onchangeLanguage={() => changeLanguage(lang)} />
                {:else if is_loading}
                    <div class="card mt-3 p-5 text-center"><Spinner /></div>
                {/if}
            </div>
        </div>
    </div>
</section>

<section class="content">
    <div class="container-fluid">
        <div class="row">
            <div class="col-12">
                <div class="card mt-3">
                    <div class="card-header">
                        <div class="card-title">{$format("properties")}</div>
                        <div class="float-end">
                            <button class="btn btn-default btn-collapse float-end" type="button" onclick={() => toggleCollapsibleItems("properties")}>
                                <i class={!showProperties ? "fas fa-angle-down" : "fas fa-angle-up"}></i>
                            </button>
                        </div>
                    </div>
                    {#if showProperties}
                        <div class="card-body" transition:fade|global={{ delay: 0, duration: 200 }}>
                            <div class="table-responsive table-readOnly">
                                {#if !is_loading_props}
                                    <Properties {properties} ptInstructs={propInstructs} />
                                {:else}
                                    <Spinner />
                                {/if}
                            </div>
                        </div>
                    {/if}
                </div>
            </div>
        </div>
    </div>
</section>

<section class="content">
    <div class="container-fluid">
        <div class="d-flex">
            <div class="card mt-3" style="flex: 2;">
                <div class="card-header">
                    <div class="card-title">{$format("page.vulnerability.note")}</div>
                    <div class="card-tools">
                        {#if !note || note.content === $format("errors.vul_no_note")}
                            <button
                                title={$format("buttons.add")}
                                class="btn btn-secondary table-button btn-add btn-xs"
                                onclick={() => (showNoteForm = true)}
                            >
                                <i class="fas fa-plus"></i>
                            </button>
                        {:else}
                            <button
                                title={$format("buttons.edit")}
                                class="btn btn-secondary table-button btn-edit btn-xs"
                                onclick={() => (showNoteForm = true)}
                            >
                                <i class="fas fa-edit"></i>
                            </button>
                            <button
                                title={$format("buttons.delete")}
                                class="btn btn-secondary table-button btn-delete btn-xs"
                                onclick={deleteNote}
                            >
                                <i class="fas fa-trash"></i>
                            </button>
                        {/if}
                    </div>
                </div>
                {#if is_loading_note}
                    <div class="card-body p-5 text-center"><Spinner /></div>
                {:else}
                    <div class="card-body" transition:fade={{ delay: 0, duration: 200 }} style="padding: 2.5em;">
                        {#if !showNoteForm}
                            <Note {note} />
                        {:else}
                            <AddNote {note} vul_id={id} on:hideNoteForm={() => (showNoteForm = false)} onhideNoteForm={() => (showNoteForm = false)} on:resetNoteWidget={() => loadNote(id)} onresetNoteWidget={() => loadNote(id)} />
                        {/if}
                    </div>
                {/if}
            </div>

            <div class="card mt-3" style="flex: 1;">
                <div class="card-header">
                    <div class="card-title">{$format("page.vulnerability.vul_tags")}</div>
                    {#if user.data.role !== "viewer"}
                        <div class="card-tools">
                            {#if !showTagsForm && !showTagsDeleteForm}
                                <div class="dropdown d-inline-block" use:clickOutside={() => (isDropdownOpen = false)}>
                                    <button
                                        type="button"
                                        title="{$format('add')} {$format('tags')}"
                                        aria-label={$format("buttons.add")}
                                        class="btn btn-secondary table-button btn-add btn-xs dropdown-toggle"
                                        onclick={() => (isDropdownOpen = !isDropdownOpen)}
                                        aria-expanded={isDropdownOpen}
                                    >
                                        <i class="fas fa-plus"></i>
                                    </button>
                                    <ul class="dropdown-menu dropdown-menu-end" class:show={isDropdownOpen} style="position: absolute; right: 0; left: auto; margin-top: 2px;">
                                        <li>
                                            <a class="dropdown-item" href="#" onclick={(e) => { e.preventDefault(); isDropdownOpen = false; showTagsForm = true; }}>{$format("buttons.select_tag.new")}</a>
                                        </li>
                                        <li>
                                            <a class="dropdown-item" href="#" onclick={(e) => { e.preventDefault(); isDropdownOpen = false; if(tagModal) tagModal.show(); }}>{$format("buttons.select_tag.add_and_associate")}</a>
                                        </li>
                                    </ul>
                                </div>
                                <button
                                    title="{$format('remove')} {$format('tags')}"
                                    aria-label="{$format('remove')} {$format('tags')}"
                                    class="btn btn-secondary table-button btn-delete btn-xs"
                                    onclick={() => (showTagsDeleteForm = true)}
                                >
                                    <i class="fas fa-trash"></i>
                                </button>
                            {:else}
                                <button
                                    title={$format("back")}
                                    aria-label={$format("back")}
                                    class="btn btn-xs"
                                    onclick={() => handleTagsReset()}
                                >
                                    <i class="fas fa-backward"></i>
                                </button>
                            {/if}
                        </div>
                    {/if}
                </div>
                <div class="card-body">
                    {#if is_loading_tags}
                        <Spinner />
                    {:else}
                        <Tag 
                            {tags} 
                            {allTags} 
                            {showTagsForm} 
                            {showTagsDeleteForm} 
                            on:submitTags={addNewTag} 
                            onsubmitTags={addNewTag}
                            on:deleteTags={handleTagDelete} 
                            ondeleteTags={handleTagDelete}
                            on:cancel={handleTagsReset} 
                            oncancel={handleTagsReset}
                        />
                    {/if}
                </div>
            </div>
        </div>
    </div>
</section>

<section class="content">
    <div class="container-fluid">
        <div class="row">
            <div class="col-12">
                <div class="findings-list">
                    {#if is_loading_findings}
                        <div class="card mt-3 pb-5" style="min-height: 25em;">
                            <div class="card-header"><div class="card-title">{$format("finding.plural")}</div></div>
                            <div class="card-body"><div class="overlay text-center pt-5 pb-5"><i class="fas fa-2x fa-sync-alt fa-spin"></i></div></div>
                        </div>
                    {:else}
                        <Finding tags={allTags} {table_is_expanded} showRefresh={true} show_expand_button={true} parent={"vulnerability"} refreshFunc={() => loadFindings(id)} on:del={() => loadVulnerability(id)} ondel={() => loadVulnerability(id)} on:showRows={showRows} onshowRows={showRows} />
                    {/if}
                </div>
            </div>
        </div>
    </div>
</section>

<!-- TAG MODAL -->
<div class="modal fade" id="tag-add-new-modal" tabindex="-1" aria-hidden="true">
    <div class="modal-dialog">
        <div class="modal-content">
            <div class="modal-header">
                <h4 class="modal-title">{$format("page.tag.add")}</h4>
                <button type="button" class="btn-close" aria-label="Close" onclick={closeTagModal}></button>
            </div>
            <div class="modal-body">
                {#key reload_tags}
                    <TagForm on:save={addNewTag} onsave={addNewTag} on:closeModal={closeTagModal} oncloseModal={closeTagModal} tag={null} />
                {/key}
            </div>
        </div>
    </div>
</div>
<!-- END TAG MODAL-->